import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import type { AuditLogItem } from '../types/audit';

export interface CreateAuthUserInput {
  name: string;
  email: string;
  password: string;
  roleId: string;
  mustChangePassword?: boolean;
  professionalId?: string;
}
export interface CreateAuthUserResult {
  uid: string;
}

export interface DeleteAuthUserInput {
  uid: string;
}
export interface DeleteAuthUserResult {
  ok: true;
}

export interface ForceUserPasswordResetInput {
  uid: string;
}
export interface ForceUserPasswordResetResult {
  ok: true;
}

// P55 — status é escrita de SERVIDOR: mexe na conta do Auth, não só no doc.
export interface SetUserStatusInput {
  uid: string;
  status: 'active' | 'disabled';
}
export interface SetUserStatusResult {
  ok: true;
}

export interface BootstrapAdminInput {
  name: string;
}
export interface BootstrapAdminResult {
  uid: string;
  roleId: string;
}

export const createAuthUser = httpsCallable<CreateAuthUserInput, CreateAuthUserResult>(
  functions,
  'createAuthUser'
);
export const deleteAuthUser = httpsCallable<DeleteAuthUserInput, DeleteAuthUserResult>(
  functions,
  'deleteAuthUser'
);
export const forceUserPasswordReset = httpsCallable<
  ForceUserPasswordResetInput,
  ForceUserPasswordResetResult
>(functions, 'forceUserPasswordReset');
export const setUserStatus = httpsCallable<SetUserStatusInput, SetUserStatusResult>(
  functions,
  'setUserStatus'
);
export const bootstrapAdmin = httpsCallable<BootstrapAdminInput, BootstrapAdminResult>(
  functions,
  'bootstrapAdmin'
);

export interface ChangeOwnPasswordInput {
  newPassword: string;
}
export interface ChangeOwnPasswordResult {
  ok: true;
}

export const changeOwnPassword = httpsCallable<ChangeOwnPasswordInput, ChangeOwnPasswordResult>(
  functions,
  'changeOwnPassword'
);

// Termos de consentimento — modelos e versões
export interface UpsertConsentTemplateInput {
  id?: string;
  name: string;
  description?: string;
  active: boolean;
  /** Confirmação explícita pra desativar modelo em uso por exame ativo (P6). */
  force?: boolean;
}
export interface UpsertConsentTemplateResult {
  ok: true;
  id: string;
}

export interface PublishConsentTemplateVersionInput {
  templateId: string;
  pdfBase64: string;
  /** Esta versão exige que TODOS reassinem (Task 4) — sobe `lastResignVersion` no
   *  modelo, na mesma escrita que `currentVersion`. Correção de texto não deve marcar. */
  requiresResign?: boolean;
}
export interface PublishConsentTemplateVersionResult {
  ok: true;
  version: number;
  sha256: string;
}

export const upsertConsentTemplate = httpsCallable<
  UpsertConsentTemplateInput,
  UpsertConsentTemplateResult
>(functions, 'upsertConsentTemplate');
export const publishConsentTemplateVersion = httpsCallable<
  PublishConsentTemplateVersionInput,
  PublishConsentTemplateVersionResult
>(functions, 'publishConsentTemplateVersion');

export interface ResendConsentInput {
  consentId: string;
}
export interface ResendConsentResult {
  ok: true;
  consentId: string;
}
export const resendConsent = httpsCallable<ResendConsentInput, ResendConsentResult>(
  functions,
  'resendConsent'
);

// Termo de LGPD avulso — disparo à mão, sem agendamento (kind: 'lgpd').
export interface SendLgpdConsentInput {
  patientId: string;
  /** `'onsite'` = tablet da recepção: cria o termo e **não envia mensagem nenhuma**.
   *  Omitido = `'whatsapp'`. */
  deliver?: 'whatsapp' | 'onsite';
}
export interface SendLgpdConsentResult {
  ok: true;
  consentId: string;
}
export const sendLgpdConsent = httpsCallable<SendLgpdConsentInput, SendLgpdConsentResult>(
  functions,
  'sendLgpdConsent'
);

/** Patch de `system_settings/consent`. Campo ausente = "não mexer". Servidor exige
 *  MANAGE_SYSTEM_SETTINGS quando o patch contém `acceptanceClauseText` (P6). */
export interface UpdateConsentSettingsInput {
  enabled?: boolean;
  whatsappTemplateName?: string;
  whatsappTemplateLanguage?: string;
  linkExpiryDays?: number;
  secondFactorMaxAttempts?: number;
  linkBaseUrl?: string | null;
  acceptanceClauseText?: string;
}
export interface UpdateConsentSettingsResult {
  ok: true;
}
export const updateConsentSettings = httpsCallable<
  UpdateConsentSettingsInput,
  UpdateConsentSettingsResult
>(functions, 'updateConsentSettings');

export interface CreateScheduleExtraInput {
  professionalId: string;
  dateIso: string;
  startTime: string;
  endTime: string;
  slotInterval: 15 | 20 | 30;
  /** Sala; omitir = qualquer sala. */
  equipmentId?: string;
  reason?: string;
}
export interface CreateScheduleExtraResult { extraId: string; }

export const createScheduleExtra = httpsCallable<CreateScheduleExtraInput, CreateScheduleExtraResult>(
  functions, 'createScheduleExtra'
);

export interface UpdateScheduleExtraInput {
  extraId: string;
  startTime?: string;
  endTime?: string;
  slotInterval?: 15 | 20 | 30;
  /** `''` limpa o motivo; omitir não mexe. NÃO passar `null` — o fio já usa `null` pra "omitido". */
  reason?: string;
  /** Sala: id = trocar · `''` = qualquer sala · omitir = não mexe (mesma regra do `reason`). */
  equipmentId?: string;
}
export interface UpdateScheduleExtraResult { ok: true; }

export const updateScheduleExtra = httpsCallable<UpdateScheduleExtraInput, UpdateScheduleExtraResult>(
  functions, 'updateScheduleExtra'
);

export interface DeleteScheduleExtraInput { extraId: string; }
export interface DeleteScheduleExtraResult { ok: true; }

export const deleteScheduleExtra = httpsCallable<DeleteScheduleExtraInput, DeleteScheduleExtraResult>(
  functions, 'deleteScheduleExtra'
);

export interface BookAppointmentManualInput {
  patientId: string;
  examId: string;
  professionalId: string;
  equipmentId: string;
  insurancePlanId: string;
  startAtIso: string;
  durationMin: number;
  paymentMethod?: string;
  requestingDoctorName?: string;
  requestingDoctorCrm?: string;
  requestRefImageUrl?: string;
  internalNotes?: string;
  updatePregnancy?: unknown;
  /** Id de schedule_extras/{id} quando o slot escolhido veio de uma janela extra. */
  createdFromExtraId?: string;
  /** Origem WA (Fase 5A) — id da conversa que deu origem ao agendamento. */
  originConversationId?: string;
}
export interface BookAppointmentManualResult {
  appointmentId: string;
  patientUpdated: boolean;
}

export const bookAppointmentManual = httpsCallable<
  BookAppointmentManualInput,
  BookAppointmentManualResult
>(functions, 'bookAppointmentManual');

export const completeAppointmentByProfessional = httpsCallable<
  { appointmentId: string },
  { ok: true }
>(functions, 'completeAppointmentByProfessional');

// ── Multi-exame: dry-run (preview) + commit atômico ─────────────────────────
export interface BatchExamInput { examId: string; durationMin: number; }
export interface BookBatchInput {
  patientId: string;
  professionalId: string;
  equipmentId?: string;
  insurancePlanId: string;
  /** Plano de desconto opcional. Quando presente, vence o convênio na tabela de preço. */
  discountPlanId?: string;
  paymentMethod?: string;
  requestingDoctorName?: string;
  requestingDoctorCrm?: string;
  requestRefImageUrl?: string;
  internalNotes?: string;
  startAtIso: string;
  exams: BatchExamInput[];
  updatePregnancy?: unknown;
  originConversationId?: string;
  commit: boolean;
}
export interface BookBatchPlacement {
  examId: string;
  examName: string;
  startAtIso: string;
  endAtIso: string;
  durationMin: number;
  priceAtBooking?: number;
}
export type BookBatchResult =
  | { fits: false; failedExam: { examId: string; name: string } }
  | { fits: true; placements: BookBatchPlacement[] }
  | { bookingGroupId?: string; appointmentIds: string[] };

export const bookAppointmentsBatch = httpsCallable<BookBatchInput, BookBatchResult>(
  functions, 'bookAppointmentsBatch',
);

export interface BookAppointmentOverbookInput {
  patientId: string;
  examId: string;
  insurancePlanId: string;
  paymentMethod?: string;
  requestingDoctorName?: string;
  requestingDoctorCrm?: string;
  requestRefImageUrl?: string;
  internalNotes?: string;
  overbookOfAppointmentId: string;
  forceBypass?: boolean;
  updatePregnancy?: unknown;
}
export interface BookAppointmentOverbookResult {
  appointmentId: string;
  patientUpdated: boolean;
}

export const bookAppointmentOverbook = httpsCallable<
  BookAppointmentOverbookInput,
  BookAppointmentOverbookResult
>(functions, 'bookAppointmentOverbook');

export interface PromoteOverbookToBaseInput {
  overbookId: string;
  baseCancelReason: string;
}
export interface PromoteOverbookToBaseResult {
  promotedAppointmentId: string;
  baseAppointmentId: string;
}

export const promoteOverbookToBase = httpsCallable<
  PromoteOverbookToBaseInput,
  PromoteOverbookToBaseResult
>(functions, 'promoteOverbookToBase');

// ====================================================================
// WhatsApp (Fase 5A)
// ====================================================================

export interface ClaimConversationInput {
  conversationId: string;
  preempt?: boolean;
}
export interface ClaimConversationResult {
  ok: boolean;
  needsPreempt?: boolean;
  currentOwner?: string;
}
export const claimConversation = httpsCallable<ClaimConversationInput, ClaimConversationResult>(
  functions,
  'claimConversation'
);

export interface FinalizeConversationInput {
  conversationId: string;
  reason?: 'manual';
}
export interface FinalizeConversationResult {
  ok: boolean;
  alreadyFinalized?: boolean;
}
export const finalizeConversation = httpsCallable<
  FinalizeConversationInput,
  FinalizeConversationResult
>(functions, 'finalizeConversation');

export interface ReopenConversationInput {
  conversationId: string;
}
export interface ReopenConversationResult {
  ok: boolean;
  alreadyOpen?: boolean;
}
export const reopenConversation = httpsCallable<
  ReopenConversationInput,
  ReopenConversationResult
>(functions, 'reopenConversation');

export interface MarkConversationReadInput {
  conversationId: string;
}
export interface MarkConversationReadResult {
  ok: boolean;
}
export const markConversationRead = httpsCallable<
  MarkConversationReadInput,
  MarkConversationReadResult
>(functions, 'markConversationRead');

export const markConversationUnread = httpsCallable<
  { conversationId: string },
  { ok: true }
>(functions, 'markConversationUnread');

export interface LinkPatientToConversationInput {
  conversationId: string;
  patientId: string;
  method?: 'search' | 'create' | 'auto_match';
}
export interface LinkPatientToConversationResult {
  ok: boolean;
}
export const linkPatientToConversation = httpsCallable<
  LinkPatientToConversationInput,
  LinkPatientToConversationResult
>(functions, 'linkPatientToConversation');

export interface UnlinkPatientFromConversationInput {
  conversationId: string;
}
export interface UnlinkPatientFromConversationResult {
  ok: boolean;
  alreadyUnlinked?: boolean;
}
export const unlinkPatientFromConversation = httpsCallable<
  UnlinkPatientFromConversationInput,
  UnlinkPatientFromConversationResult
>(functions, 'unlinkPatientFromConversation');

export interface TransferConversationInput {
  conversationId: string;
  toUid: string;
}
export interface TransferConversationResult {
  ok: boolean;
  alreadyAssigned?: boolean;
}
export const transferConversation = httpsCallable<
  TransferConversationInput,
  TransferConversationResult
>(functions, 'transferConversation');

export interface LinkMediaToAppointmentInput {
  conversationId: string;
  messageId: string;
  appointmentId: string;
}
export interface LinkMediaToAppointmentResult {
  ok: boolean;
}
export const linkMediaToAppointment = httpsCallable<
  LinkMediaToAppointmentInput,
  LinkMediaToAppointmentResult
>(functions, 'linkMediaToAppointment');

export interface UnlinkMediaFromAppointmentInput {
  conversationId: string;
  messageId: string;
  appointmentId: string;
}
export interface UnlinkMediaFromAppointmentResult {
  ok: boolean;
}
export const unlinkMediaFromAppointment = httpsCallable<
  UnlinkMediaFromAppointmentInput,
  UnlinkMediaFromAppointmentResult
>(functions, 'unlinkMediaFromAppointment');

export interface SendTemplateInput {
  conversationId: string;
  templateName: string;
  language?: string;
  manual?: string[];
  overrides?: Record<string, string>;
}
export interface SendTemplateResult {
  ok: boolean;
  messageId: string;
}
export const sendTemplate = httpsCallable<SendTemplateInput, SendTemplateResult>(
  functions,
  'sendTemplate'
);

export interface StartConversationInput {
  phone: string;
  patientId?: string;
  customerName?: string;
  templateName: string;
  language?: string;
  manual?: string[];
  overrides?: Record<string, string>;
  acknowledgeOptOut?: boolean;
}
export interface StartConversationResult {
  conversationId: string;
  sentTemplate: boolean;
  alreadyOpen: boolean;
  messageId?: string;
}
export const startConversation = httpsCallable<
  StartConversationInput,
  StartConversationResult
>(functions, 'startConversation');

export type UserAvailabilityStatus = 'available' | 'away';
export type OpenConvAwayAction = 'keep' | 'to_triage' | 'to_user';
export interface SetUserAvailabilityInput {
  targetUid?: string;
  status: UserAvailabilityStatus;
  awayMessage?: string;
  awayUntilMs?: number;
  openConvAction?: OpenConvAwayAction;
  routeAwayTo?: string;
}
export interface SetUserAvailabilityResult {
  ok: boolean;
  affectedConversationsCount: number;
}
export const setUserAvailability = httpsCallable<
  SetUserAvailabilityInput,
  SetUserAvailabilityResult
>(functions, 'setUserAvailability');

export interface TestWaConnectionResult {
  ok: boolean;
  phoneNumber?: string;
  displayName?: string;
  error?: string;
}
export const testWaConnection = httpsCallable<undefined, TestWaConnectionResult>(
  functions,
  'testWaConnection'
);

export interface SyncWaTemplatesResult {
  ok: boolean;
  added?: number;
  updated?: number;
  removed?: number;
  error?: string;
}
export const syncWaTemplates = httpsCallable<undefined, SyncWaTemplatesResult>(
  functions,
  'syncWaTemplates'
);

export interface UpdateTemplateMappingsInput {
  templateId: string;
  /** Estrutura livre: { header?, body?: Mapping[], buttons?: Record<string, Mapping> } */
  variableMappings?: unknown;
  internalCategory?: string;
  isFavorite?: boolean;
}
export interface UpdateTemplateMappingsResult {
  ok: boolean;
}
export const updateTemplateMappings = httpsCallable<
  UpdateTemplateMappingsInput,
  UpdateTemplateMappingsResult
>(functions, 'updateTemplateMappings');

export interface DeleteWaTemplateInput {
  templateId: string;
}
export interface DeleteWaTemplateResult {
  ok: boolean;
}
export const deleteWaTemplate = httpsCallable<
  DeleteWaTemplateInput,
  DeleteWaTemplateResult
>(functions, 'deleteWaTemplate');

// ====================================================================
// Patients — hard delete (v0.13.1, admin-only)
// ====================================================================

export interface HardDeletePatientInput {
  patientId: string;
  reason: string;
  confirmDisplayName: string;
}
export interface HardDeletePatientResult {
  ok: true;
}
export const hardDeletePatient = httpsCallable<
  HardDeletePatientInput,
  HardDeletePatientResult
>(functions, 'hardDeletePatient');

// ====================================================================
// WhatsApp — multi-paciente (v0.14)
// ====================================================================

export interface SetActivePatientInput {
  conversationId: string;
  patientId: string;
}
export interface SetActivePatientResult {
  ok: true;
}
export const setActivePatient = httpsCallable<
  SetActivePatientInput,
  SetActivePatientResult
>(functions, 'setActivePatient');

// ====================================================================
// Audit logs (Fase 10)
// ====================================================================

export interface ListAuditLogsInput {
  startDate: string;
  endDate: string;
  module?: string;
  action?: string;
  userId?: string;
  entityId?: string;
  cursor?: string;
}
export interface ListAuditLogsResult {
  items: AuditLogItem[];
  nextCursor: string | null;
  done: boolean;
  scannedCap: boolean;
}
export const listAuditLogs = httpsCallable<ListAuditLogsInput, ListAuditLogsResult>(
  functions,
  'listAuditLogs',
);

// ── Pregnancy episodes (A.5 Spec 1) ──
// Datas vão como strings ISO; o backend (Zod) converte pra Timestamp.
export interface PregnancyDatingInput {
  adopted: 'dum' | 'us' | 'manual';
  lmp?: string | null;                  // ISO (yyyy-mm-dd ou ISO completa)
  usAnchor?: {
    atDate: string;
    weeks: number;
    days: number;
    basedOn: 'CRL' | 'GSD' | 'BPD' | 'HC' | 'other';
  } | null;
  manual?: { atDate: string; weeks: number; days: number } | null;
}
export interface OpenOrUpdatePregnancyInput {
  patientId: string;
  mode: 'new' | 'update';
  dating: PregnancyDatingInput;
  multiplicity?: 'single' | 'twin' | 'triplet_plus' | null;
  priorOutcome?: { kind: 'parto' | 'perda' | 'outro'; date?: string | null; note?: string | null } | null;
  /** Opcional: se fornecido, o backend carimbará o appointment com pregnancyId + gaAtExamDate. */
  appointmentId?: string;
}
export interface OpenOrUpdatePregnancyResult {
  ok: true;
  pregnancyId: string;
}
export const openOrUpdatePregnancy = httpsCallable<
  OpenOrUpdatePregnancyInput,
  OpenOrUpdatePregnancyResult
>(functions, 'openOrUpdatePregnancy');

export interface ClosePregnancyInput {
  patientId: string;
  outcome: { kind: 'parto' | 'perda' | 'outro'; date?: string | null; note?: string | null };
}
export interface ClosePregnancyResult {
  ok: true;
}
export const closePregnancy = httpsCallable<ClosePregnancyInput, ClosePregnancyResult>(
  functions,
  'closePregnancy',
);

// ── Lista de espera ──────────────────────────────────────────────────────────
export interface OfferWaitlistSlotInput {
  entryId: string;
  slotLabel: string;
}
export interface OfferWaitlistSlotResult {
  ok: true;
}
export const offerWaitlistSlot = httpsCallable<OfferWaitlistSlotInput, OfferWaitlistSlotResult>(
  functions,
  'offerWaitlistSlot',
);

export interface SaveWaitlistSettingsInput {
  offerEnabled: boolean;
  offerTemplateName: string;
  offerTemplateLanguage: string;
}
export interface SaveWaitlistSettingsResult {
  ok: true;
}
export const saveWaitlistSettings = httpsCallable<
  SaveWaitlistSettingsInput,
  SaveWaitlistSettingsResult
>(functions, 'saveWaitlistSettings');

// ── Vagas vista Semana/Mês — contagem de slots disponíveis por dia ──
export interface GetAvailabilityCountsInput {
  professionalId: string;
  fromDateIso: string;
  toDateIso: string;
  examDurationMin: number;
}
export interface GetAvailabilityCountsResult {
  counts: Array<{ dateIso: string; count: number }>;
}
export const getAvailabilityCounts = httpsCallable<
  GetAvailabilityCountsInput,
  GetAvailabilityCountsResult
>(functions, 'getAvailabilityCounts');

// ── Rastreio 1º tri em gemelar (P7) ───────────────────────────────────────────
export interface SetPregnancyChorionicityInput {
  patientId: string;
  pregnancyId: string;
  /** Omitir pra uma escrita SÓ de zigosidade (troca de zigosidade não pode carregar uma
   *  corionicidade requentada da tela e sobrescrever o que outro médico gravou — o servidor
   *  pula por completo a guarda/escrita de corionicidade quando `value` está ausente). */
  value?: 'mono' | 'di';
  gaDaysAtSet?: number;
  /** Só em dicoriônica — mono é monozigótica por definição e o servidor recusa qualquer outro
   *  valor. Mesma proteção de discordância que `value`: já registrada com valor diferente exige
   *  `confirmOverride: true`, senão `failed-precondition`. */
  zygosity?: 'monozygotic' | 'dizygotic' | 'unknown';
  confirmOverride?: boolean;
}
export interface SetPregnancyChorionicityResult { ok: true; }
export const setPregnancyChorionicity = httpsCallable<
  SetPregnancyChorionicityInput,
  SetPregnancyChorionicityResult
>(functions, 'setPregnancyChorionicity');

// P92 — sessão por vaga (computador/celular)
export type SessionSlotInput = 'computador' | 'celular';
export interface ClaimSessionInput {
  slot: SessionSlotInput;
  force?: boolean | null;
  userAgent?: string | null;
}
export type ClaimSessionResult =
  | { status: 'ok'; claimedAt: number }
  | { status: 'exempt' }
  | { status: 'conflict'; since: number; userAgent: string | null };

export const claimSession = httpsCallable<ClaimSessionInput, ClaimSessionResult>(
  functions,
  'claimSession'
);
export const releaseSession = httpsCallable<{ slot: SessionSlotInput }, { released: boolean }>(
  functions,
  'releaseSession'
);

// Gaius 2b — exame AVULSO (plano Laudo / exame externo): agendamento já `completed`, sem grade nem aparelho.
export interface CreateWalkInExamInput {
  patientId: string;
  examId: string;
  professionalId: string;
  insurancePlanId: string;
  /** YYYY-MM-DD, hoje ou passado (vira 12:00 de Brasília). */
  examDateIso: string;
}
export const createWalkInExam = httpsCallable<CreateWalkInExamInput, { appointmentId: string }>(
  functions,
  'createWalkInExam'
);
